import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import useAdminAccess from '../../../context/useAdminAccess';
import ActivityList from '../../profile/activity/ActivityList';
import { fetchActivity } from '../../profile/activity/activityApi';
import '../../profile/activity/activity.css';

const RECENT = 10;

/**
 * "Recent activity" in the admin user drawer: the last ten timeline entries
 * for the selected person, with a link to the full, filterable timeline on
 * their profile. Needs profiles.activity; an administrator's timeline is only
 * shown to full admins (the server refuses it to anyone else).
 */
export default function UserActivitySection({ user }) {
  const { can, isFullAdmin } = useAdminAccess();
  const userId = user?.id;
  const allowed = !!user && can('profiles.activity') && (!user.is_admin || isFullAdmin);
  const [state, setState] = useState({ kind: 'loading', entries: [] });

  useEffect(() => {
    if (!allowed) return undefined;
    let active = true;
    setState({ kind: 'loading', entries: [] });
    fetchActivity({ userId, limit: RECENT })
      .then((page) => { if (active) setState({ kind: 'ok', entries: page.entries || [] }); })
      .catch(() => { if (active) setState({ kind: 'error', entries: [] }); });
    return () => { active = false; };
  }, [allowed, userId]);

  if (!allowed) return null;

  return (
    <section className="ut-activity" aria-labelledby="ut-activity-title">
      <h3 id="ut-activity-title" className="ut-activity__title">Recent activity</h3>
      {state.kind === 'loading' && (
        <p className="ut-activity__muted" aria-busy="true" aria-label="Loading recent activity">Loading…</p>
      )}
      {state.kind === 'error' && <p className="ut-activity__muted">Couldn&apos;t load recent activity.</p>}
      {state.kind === 'ok' && state.entries.length === 0 && <p className="ut-activity__muted">No activity yet.</p>}
      {state.kind === 'ok' && state.entries.length > 0 && (
        <>
          <ActivityList entries={state.entries} compact canReviewApps={can('apps.view')} />
          <Link className="ut-activity__more" to={`/profile/${userId}?tab=activity`}>Open full timeline →</Link>
        </>
      )}
    </section>
  );
}
